import React, { useState } from 'react';
import { useFormik } from 'formik';
import { useDispatch, useSelector } from 'react-redux';
import * as yup from 'yup';
import { Link, useNavigate } from 'react-router-dom';
import Logo from '../../components/Logo';
import { login, getCurrentAccount } from '../../utils/actions';

const LoginSchema = yup.object().shape({
  email: yup.string().email('Email is invalid').required('Email address is required'),
  password: yup.string().required('Password is required'),
});

const Login = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { loading, error } = useSelector((state) => state.auth);
  const [submitError, setSubmitError] = useState(null);

  const formik = useFormik({
    initialValues: {
      email: '',
      password: '',
    },
    validationSchema: LoginSchema,
    onSubmit: async (values, { setSubmitting }) => {
      setSubmitError(null);

      try
      {
        localStorage.clear();
        const response = await dispatch(
          login({
            email: values.email,
            password_hash: values.password,
          })
        );

        if (response)
        {
          await dispatch(getCurrentAccount());
          const account = localStorage.getItem('account') ? JSON.parse(localStorage.getItem('account')) : {};
          const hasProfile = account.profiles && account.profiles.length > 0;
          const pendingProfile = hasProfile ? account.profiles.find((profile) => profile.status === 'pending' && profile.account_id === account.id) : undefined;
          const contactInfo = account.profiles.find((profile) => profile.entity_kyc_set === 1 && profile.account_id === account.id);
          const registrationCertificateProfile = account.profiles.find((profile) => profile.entity_reg_uploaded === 1 && profile.account_id === account.id);
          const registrationTaxCertificateProfile = account.profiles.find((profile) => profile.entity_tax_uploaded === 1 && profile.account_id === account.id);
          const pacraPrintOutProfile = account.profiles.find((profile) => profile.entity_pacra_printout_uploaded === 1 && profile.account_id === account.id);
          const registrationCompanyProfile = account.profiles.find((profile) => profile.entity_profile_uploaded === 1 && profile.account_id === account.id);
          const registrationBoardResolution = account.profiles.find((profile) => profile.entity_resolution_uploaded === 1 && profile.account_id === account.id);
          const registrationDirectorDetails = account.profiles.find((profile) => profile.entity_owners_uploaded === 1 && profile.account_id === account.id);
          const registrationBankDetails = account.profiles.find((profile) => profile.bank_account_set === 1 && profile.account_id === account.id);
          const registrationDeclaration = account.profiles.find((profile) => profile.bank_account_set === 1 && profile.entity_set !== 1 && profile.account_id === account.id);
          const registrationVerification = account.profiles.find((profile) => profile.entity_set === 0 && profile.account_id === account.id);
          const zamaceProfile = account.profiles.find(profile => profile.type === "exchange");

          let nextPage = null;

          // if (zamaceProfile && account.active === 1)
          // {
          //   nextPage = '/exchange';
          // } else 
          if (hasProfile && account.active === 1)
          {
            nextPage = '/home';
          } else if (account.verified === 0)
          {
            nextPage = '/verify_account';
          } else if (!hasProfile)
          {
            nextPage = '/membership_type';
          } else if (hasProfile && pendingProfile)
          {
            if (hasProfile && !contactInfo)
            {
              nextPage = '/complete_entity_kyc';
            } else if (hasProfile && !registrationCertificateProfile)
            {
              nextPage = '/registration_certificate';
            } else if (hasProfile && contactInfo && registrationCertificateProfile && !registrationTaxCertificateProfile)
            {
              nextPage = '/registration_tax_certificate';
            }
            else if (hasProfile && contactInfo && registrationCertificateProfile && registrationTaxCertificateProfile && !pacraPrintOutProfile)
            {
              nextPage = '/registration_pacra_printout';
            }
            else if (
              hasProfile &&
              contactInfo &&
              registrationCertificateProfile &&
              registrationTaxCertificateProfile &&
              pacraPrintOutProfile &&
              !registrationCompanyProfile
            )
            {
              nextPage = '/organization_profile';
            } else if (
              hasProfile &&
              contactInfo &&
              registrationCertificateProfile &&
              registrationTaxCertificateProfile &&
              pacraPrintOutProfile &&
              registrationCompanyProfile &&
              !registrationBoardResolution
            )
            {
              nextPage = '/board_resolution_document';
            } else if (
              hasProfile &&
              contactInfo &&
              registrationCertificateProfile &&
              registrationTaxCertificateProfile &&
              pacraPrintOutProfile &&
              registrationCompanyProfile &&
              registrationBoardResolution &&
              !registrationDirectorDetails
            )
            {
              nextPage = '/director_details';
            } else if (
              hasProfile &&
              contactInfo &&
              registrationCertificateProfile &&
              registrationTaxCertificateProfile &&
              pacraPrintOutProfile &&
              registrationCompanyProfile &&
              registrationBoardResolution &&
              registrationDirectorDetails &&
              !registrationBankDetails
            )
            {
              nextPage = '/bank_details';
            } else if (
              hasProfile &&
              contactInfo &&
              registrationCertificateProfile &&
              registrationTaxCertificateProfile &&
              pacraPrintOutProfile &&
              registrationCompanyProfile &&
              registrationBoardResolution &&
              registrationDirectorDetails &&
              registrationBankDetails &&
              !registrationDeclaration
            )
            {
              nextPage = '/registration_declaration';
            } else if (
              hasProfile &&
              contactInfo &&
              registrationCertificateProfile &&
              registrationTaxCertificateProfile &&
              pacraPrintOutProfile &&
              registrationCompanyProfile &&
              registrationBoardResolution &&
              registrationDirectorDetails &&
              registrationBankDetails &&
              registrationDeclaration &&
              registrationVerification
            )
            {
              nextPage = '/registration_verification';
            }
          }

          if (nextPage)
          {
            navigate(nextPage);
          }
        }
      } catch (error)
      {
        setSubmitError(error.response?.data?.error || 'An error occurred during login.');
        setSubmitting(false);
      } finally
      {
        setSubmitting(false);
      }
    },
  });

  return (
    <section className="bg-gray-50 dark:bg-gray-900">
      <div className="flex flex-col items-center justify-center px-6 py-8 mx-auto md:h-screen lg:py-0">
        <Logo />
        <div className="w-full bg-white rounded-lg shadow dark:border md:mt-0 sm:max-w-md xl:p-0 dark:bg-gray-800 dark:border-gray-700">
          <div className="p-6 space-y-4 md:space-y-6 sm:p-8">
            <h1 className="text-xl font-bold leading-tight tracking-tight text-gray-900 md:text-2xl dark:text-white">
              Sign in to your account
            </h1>
            <form className="space-y-4 md:space-y-6" onSubmit={formik.handleSubmit}>
              <div>
                <label htmlFor="email" className="block mb-2 text-sm font-medium text-gray-900 dark:text-white">
                  Your email
                </label>
                <input
                  type="email"
                  name="email"
                  id="email"
                  className="bg-gray-50 border border-gray-300 text-gray-900 sm:text-sm rounded-lg focus:ring-primary-600 focus:border-primary-600 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-green-500 dark:focus:border-green-500"
                  placeholder="name@company.com"
                  required=""
                  onChange={formik.handleChange}
                  value={formik.values.email}
                />
                {formik.errors.email && formik.touched.email && (
                  <div className="p-2 mb-4 text-sm text-red-800 dark:text-red-400">{formik.errors.email}</div>
                )}
              </div>
              <div>
                <label htmlFor="password" className="block mb-2 text-sm font-medium text-gray-900 dark:text-white">
                  Password
                </label>
                <input
                  type="password"
                  name="password"
                  id="password"
                  placeholder="••••••••"
                  className="bg-gray-50 border border-gray-300 text-gray-900 sm:text-sm rounded-lg focus:ring-primary-600 focus:border-primary-600 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-green-500 dark:focus:border-green-500"
                  required=""
                  onChange={formik.handleChange}
                  value={formik.values.password}
                />
                {formik.errors.password && formik.touched.password && (
                  <div className="p-2 mb-4 text-sm text-red-800 dark:text-red-400">{formik.errors.password}</div>
                )}
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-start">
                  <div className="flex items-center h-5">
                    <input
                      id="remember"
                      aria-describedby="remember"
                      type="checkbox"
                      className="w-4 h-4 border border-gray-300 rounded bg-gray-50 focus:ring-3 focus:ring-primary-300 dark:bg-gray-700 dark:border-gray-600 dark:focus:ring-primary-600 dark:ring-offset-gray-800"
                      required=""
                    />
                  </div>
                  <div className="ml-3 text-sm">
                    <label htmlFor="remember" className="text-gray-500 dark:text-gray-300">
                      Remember me
                    </label>
                  </div>
                </div>
                <a href="/reset_password" className="text-sm font-medium text-primary-600 hover:underline dark:text-primary-500">
                  Forgot password?
                </a>
              </div>

              <button
                type="submit"
                className="w-full text-white bg-primary-600 hover:bg-primary-700 focus:ring-4 focus:outline-none focus:ring-primary-300 font-medium rounded-lg text-sm px-5 py-2.5 text-center dark:bg-primary-600 dark:hover:bg-primary-700 dark:focus:ring-primary-800"
                disabled={formik.isSubmitting}
              >
                {formik.isSubmitting ? 'Signing in...' : 'Sign in'}
              </button>

              {submitError && (
                <div className="p-4 mb-4 text-sm text-red-800 rounded-lg bg-red-50 dark:bg-gray-800 dark:text-red-400" role="alert">
                  <span className="font-medium">Error: </span>
                  {submitError}
                </div>
              )}
              {/* 
              {error && error.length > 0 && (
                <div className="p-4 mb-4 text-sm text-red-800 rounded-lg bg-red-50 dark:bg-gray-800 dark:text-red-400" role="alert">
                  <span className="font-medium">Error: </span>
                  {error}
                </div>
              )} */}

              {loading && (
                <div className="text-center">
                  <div role="status">
                    <svg
                      aria-hidden="true"
                      className="inline w-8 h-8 mr-2 text-gray-200 animate-spin dark:text-gray-600 fill-green-600"
                      viewBox="0 0 100 101"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                        fill="currentColor"
                      />
                      <path
                        d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                        fill="currentFill"
                      />
                    </svg>

                    <span className="sr-only">Please wait...</span>
                  </div>
                </div>
              )}

              <p className="text-sm font-light text-gray-500 dark:text-gray-400">
                Don’t have an account yet?{' '}
                <Link to="/register" className="font-medium text-primary-600 hover:underline dark:text-primary-500">
                  Sign up
                </Link>
              </p>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Login;
